/**
 * Assistant anchor spotlight (#2171).
 *
 * After the chatbot navigates the user to a page, this scrolls to the section
 * annotated with `data-assistant-anchor="<key>"` and briefly spotlights it so
 * the user sees exactly where the assistant is pointing. Degrades silently:
 * if the element never mounts (removed section, role-gated, slow page), the
 * user simply lands on the page — never an error.
 *
 * Timing uses setTimeout, NOT requestAnimationFrame: rAF never fires in a
 * hidden/backgrounded tab, which would freeze the poll forever.
 */

export const ANCHOR_SPOTLIGHT_CLASS = 'assistant-anchor-spotlight';

const POLL_INTERVAL_MS = 200;
const POLL_TIMEOUT_MS = 6_000;
const SPOTLIGHT_DURATION_MS = 2_600;

/**
 * Poll for the anchored element (it usually mounts after route transition and
 * data fetch), then scroll it into view and pulse the spotlight class.
 * Returns a cancel function.
 */
export function scrollToAssistantAnchor(anchorKey: string): () => void {
  if (typeof window === 'undefined' || !anchorKey) return () => {};

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const startedAt = Date.now();

  const attempt = () => {
    if (cancelled) return;

    const element = document.querySelector<HTMLElement>(
      `[data-assistant-anchor="${anchorKey}"]`
    );

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add(ANCHOR_SPOTLIGHT_CLASS);
      timer = setTimeout(() => {
        element.classList.remove(ANCHOR_SPOTLIGHT_CLASS);
      }, SPOTLIGHT_DURATION_MS);
      return;
    }

    if (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      timer = setTimeout(attempt, POLL_INTERVAL_MS);
    }
    // Timeout: degrade silently — plain navigation already happened.
  };

  // First attempt is deferred a tick so router.push has begun rendering.
  timer = setTimeout(attempt, POLL_INTERVAL_MS);

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}
